import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AppThunk } from '../store'
import { setErrors } from '../common/errorsSlice'

import {
  RequestApplicationDetail,
  RequestApplicationFavorite,
  RequestReportDetail,
  ResponseApplicationDetail,
  ResponseApplicationList,
  ResponseReportApplicationList,
  ResponseReportApplicationMember,
  ResponseReportPrint,
  ResponseReportWorkStyle,
} from '../../types/analyze'
import {
  getApplicationDetail,
  getApplicationList,
  postApplicationFavorite,
  deleteApplicationFavorite,
  getReportWorkStyle,
  getReportPrint,
  getReportApplicationList,
  getReportApplicationMember,
  getApplicationFrequency,
} from '../../apis/analyze'

interface State {
  isLoading: boolean
  isFrequencyLoading: boolean
  applicationList: ResponseApplicationList
  applicationDetail: ResponseApplicationDetail
  reportPrint: ResponseReportPrint
  reportWorkStyle: ResponseReportWorkStyle
  reportApplicationList: ResponseReportApplicationList
  reportApplicationMember: ResponseReportApplicationMember
}

const initialState: Partial<State> = {
  isLoading: true,
  isFrequencyLoading: true,
  applicationList: undefined,
  applicationDetail: undefined,
  reportPrint: undefined,
  reportWorkStyle: undefined,
  reportApplicationList: undefined,
  reportApplicationMember: undefined,
}

const analyzeSlice = createSlice({
  name: 'analyze',
  initialState,
  reducers: {
    requestStart: (state) => {
      state.isLoading = true
      return state
    },
    requestStartForFrequency: (state) => {
      state.isFrequencyLoading = true
      return state
    },
    buildApplicationList: (state, action: PayloadAction<ResponseApplicationList>) => {
      state.isLoading = false
      state.applicationList = action.payload
      return state
    },
    buildApplicationFavorite: (state, action: PayloadAction<{name: string}>) => {
      if (!state.applicationList) return state
      state.applicationList = {
        company_services: state.applicationList.company_services.map((value) => (
          value.name === action.payload.name ? { ...value, favorite: !value.favorite } : value
        )),
      }
      return state
    },
    buildApplicationDetail: (state, action: PayloadAction<ResponseApplicationDetail>) => {
      state.isLoading = false
      state.applicationDetail = {
        ...state.applicationDetail,
        summary: action.payload.summary,
        tendency: action.payload.tendency,
        word: action.payload.word,
      }
      return state
    },
    buildApplicationFrequency: (state, action: PayloadAction<ResponseApplicationDetail>) => {
      state.isFrequencyLoading = false
      state.applicationDetail = {
        ...state.applicationDetail,
        frequency: action.payload.frequency,
      }
      return state
    },
    buildReportPrint: (state, action: PayloadAction<ResponseReportPrint>) => {
      state.isLoading = false
      state.reportPrint = action.payload
      return state
    },
    buildReportWorkStyle: (state, action: PayloadAction<ResponseReportWorkStyle>) => {
      state.isLoading = false
      state.reportWorkStyle = action.payload
      return state
    },
    buildReportApplicationList: (state, action: PayloadAction<ResponseReportApplicationList>) => {
      state.isLoading = false
      state.reportApplicationList = action.payload
      return state
    },
    buildReportApplicationMember: (state, action: PayloadAction<ResponseReportApplicationMember>) => {
      state.isLoading = false
      state.reportApplicationMember = action.payload
      return state
    },
    resetState: (state) => {
      // eslint-disable-next-line no-param-reassign
      state = initialState
      return state
    },
  },
})

export const {
  requestStart,
  requestStartForFrequency,
  buildApplicationList,
  buildApplicationFavorite,
  buildApplicationDetail,
  buildApplicationFrequency,
  buildReportPrint,
  buildReportWorkStyle,
  buildReportApplicationList,
  buildReportApplicationMember,
  resetState,
} = analyzeSlice.actions

export const analyzeReducer = analyzeSlice.reducer

export const fetchApplicationList = (
  param: RequestReportDetail,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  getApplicationList(param)
    .then((result: ResponseApplicationList) => {
      dispatch(buildApplicationList(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchApplicationDetail = (
  param: RequestApplicationDetail,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  getApplicationDetail(param)
    .then((result: ResponseApplicationDetail) => {
      dispatch(buildApplicationDetail(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchApplicationFrequency = (
  param: RequestApplicationDetail,
): AppThunk => async (dispatch) => {
  dispatch(requestStartForFrequency())
  getApplicationFrequency(param)
    .then((result: ResponseApplicationDetail) => {
      dispatch(buildApplicationFrequency(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const addApplicationFavorite = (
  param: RequestApplicationFavorite,
  callback: () => void,
): AppThunk => async (dispatch) => {
  postApplicationFavorite(param)
    .then(() => {
      dispatch(buildApplicationFavorite({ name: param.service_name }))
      callback()
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const delApplicationFavorite = (
  param: RequestApplicationFavorite,
  callback: () => void,
): AppThunk => async (dispatch) => {
  deleteApplicationFavorite(param)
    .then(() => {
      dispatch(buildApplicationFavorite({ name: param.service_name }))
      callback()
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchReportPrint = (
  param: RequestReportDetail,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  getReportPrint(param)
    .then((result: ResponseReportPrint) => {
      dispatch(buildReportPrint(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchReportWorkStyle = (
  param: RequestReportDetail,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  getReportWorkStyle(param)
    .then((result: ResponseReportWorkStyle) => {
      dispatch(buildReportWorkStyle(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchReportApplicationList = (
  param: RequestReportDetail,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  getReportApplicationList(param)
    .then((result: ResponseReportApplicationList) => {
      dispatch(buildReportApplicationList(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchReportApplicationMember = (
  param: RequestReportDetail,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  getReportApplicationMember(param)
    .then((result: ResponseReportApplicationMember) => {
      dispatch(buildReportApplicationMember(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}
